import { createFileRoute, useRouter } from "@tanstack/solid-router";
import { requireFeature } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";
import { ExternalLink } from "lucide-solid";
import { createQuery } from "@tanstack/solid-query";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  Show,
} from "solid-js";
import { Crosshair, Loader2, Search, X } from "lucide-solid";
import { PendingChangesButton } from "~/components/pending-changes-button";
import { rankingsApi, playersApi, configApi } from "~/lib/api";
import { PageContainer, PageHeader } from "~/components/layout/page";
import { TierLadder } from "~/components/ranking-table";
import { huntRanking, rankedRankings } from "~/lib/profile-utils";
import {
  RACE_TYPES,
  CAR_CLASSES,
  TIER_ORDER,
  type RaceType,
  type CarClass,
} from "system";
import { RACE_TYPE_LABELS, CAR_CLASS_LABELS, cn } from "~/lib/utils";
import { useAuth } from "~/lib/auth";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { ToggleChip } from "~/components/ui/toggle-chip";
import { Dynamic } from "solid-js/web";

function validateEnum<T extends string>(
  value: unknown,
  options: readonly T[],
): T | undefined {
  return (options as unknown as string[]).includes(value as string)
    ? (value as T)
    : undefined;
}

let staggerButton: HTMLElement | undefined;;

export const Route = createFileRoute("/_authed/rankings/")({
  beforeLoad: () => requireFeature("rankings"),
  validateSearch: (search: Record<string, unknown>) => ({
    rt: validateEnum(search.rt, [...RACE_TYPES, "all" as const]),
    cc: validateEnum(search.cc, [...CAR_CLASSES, "all" as const]),
    q:
      typeof search.q === "string" && search.q.length > 0
        ? search.q
        : undefined,
  }),
  component: RankingsPage,
});

function RankingsPage() {
  const { state } = useAuth();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();

  // Search input: local signal for snappy cursor-stable typing; syncs one-way to URL.
  // The URL sync bypasses router.navigate to avoid the full navigation cycle
  // (beforeLoad re-run, onRendered scroll restoration, match reconciliation), which
  // remounts the route subtree and defocuses the input mid-typing.
  const [searchInput, setSearchInput] = createSignal(search().q ?? "");
  const activeSearch = createMemo(() => {
    const s = searchInput().trim();
    return /^[a-zA-Z0-9_.]{3,}$/.test(s) ? s : "";
  });
  createEffect(
    on(
      activeSearch,
      (q) => {
        const url = new URL(window.location.href);
        if (q) url.searchParams.set("q", q);
        else url.searchParams.delete("q");
        const history = router.history as unknown as { _ignoreSubscribers?: boolean };
        const prev = history._ignoreSubscribers;
        history._ignoreSubscribers = true;
        try {
          window.history.replaceState(window.history.state, "", url.toString());
        } finally {
          history._ignoreSubscribers = prev;
        }
      },
      { defer: true },
    ),
  );

  const profile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id,
    staleTime: 5 * 60_000,
  }));

  // null  → admin/judge: no pre-selection (show all)
  // black → user's lowest-tier category instead of highest
  const defaultFilter = createMemo(
    (): { raceType: RaceType; carClass: CarClass } | null => {
      if (state.user?.role === "admin" || state.user?.role === "judge")
        return null;
      const ranked = rankedRankings(profile.data?.rankings ?? []);
      if (!ranked.length) return { raceType: "street", carClass: "s1" };
      const best = ranked.reduce((a, b) =>
        TIER_ORDER[a.tier] >= TIER_ORDER[b.tier] ? a : b,
      );
      if (best.tier === "black") {
        const lowest = ranked.reduce((a, b) =>
          TIER_ORDER[a.tier] <= TIER_ORDER[b.tier] ? a : b,
        );
        return { raceType: lowest.raceType, carClass: lowest.carClass };
      }
      return { raceType: best.raceType, carClass: best.carClass };
    },
  );

  // URL params: "all" = explicitly cleared, undefined = use computed default, value = explicit
  // Read search() directly (not each other) to avoid mutual recursion when both are undefined.
  const activeRaceType = (): RaceType | undefined => {
    if (activeSearch()) return undefined;
    const rt = search().rt;
    if (rt === "all") return undefined;
    if (rt !== undefined) return rt;
    const cc = search().cc;
    if (cc !== undefined && cc !== "all")
      return (
        RACE_TYPES.find((c) => hasData(c, cc)) ?? defaultFilter()?.raceType
      );
    return defaultFilter()?.raceType;
  };

  const activeCarClass = (): CarClass | undefined => {
    if (activeSearch()) return undefined;
    const cc = search().cc;
    if (cc === "all") return undefined;
    if (cc !== undefined) return cc;
    const rt = search().rt;
    if (rt !== undefined && rt !== "all")
      return (
        CAR_CLASSES.find((c) => hasData(rt, c)) ?? defaultFilter()?.carClass
      );
    return defaultFilter()?.carClass;
  };

  // Highest non-black, non-white category → where challengers exist in the table
  const huntFilter = createMemo(
    (): { raceType: RaceType; carClass: CarClass } | null => {
      if (!profile.data) return null;
      const hr = huntRanking(profile.data);
      if (!hr || hr.tier === "white") return null;
      return { raceType: hr.raceType, carClass: hr.carClass };
    },
  );

  const isHuntActive = () => {
    const h = huntFilter();
    return (
      h !== null &&
      activeRaceType() === h.raceType &&
      activeCarClass() === h.carClass
    );
  };

  const config = createQuery(() => ({
    queryKey: [QueryKey.Config],
    queryFn: () => configApi.get(),
    staleTime: Infinity,
  }));

  const counts = createQuery(() => ({
    queryKey: [QueryKey.Rankings, "counts"],
    queryFn: () => rankingsApi.counts(),
    staleTime: 60_000,
  }));

  const hasData = (rt: RaceType, cc: CarClass) =>
    counts.data?.includes(`${rt}:${cc}`) ?? true;

  const groups = createQuery(() => ({
    queryKey: activeSearch()
      ? [QueryKey.Rankings, "groups", "search", activeSearch()]
      : [QueryKey.Rankings, "groups"],
    queryFn: () =>
      activeSearch()
        ? rankingsApi.groups({ search: activeSearch() })
        : rankingsApi.groups(),
    staleTime: 5 * 60_000,
  }));

  const filteredGroups = createMemo(() => {
    if (activeSearch()) return groups.data ?? [];
    const data = groups.data ?? [];
    const rt = activeRaceType();
    const cc = activeCarClass();
    return data.filter(
      (g) => (!rt || g.raceType === rt) && (!cc || g.carClass === cc),
    );
  });

  const clearSearch = () => {
    setSearchInput("");
  };

  const selectRaceType = (type: RaceType) => {
    clearSearch();
    navigate({
      search: (prev) => ({ ...prev, rt: type, q: undefined }),
      replace: true,
    });
  };
  const clearRaceType = () => {
    clearSearch();
    navigate({
      search: (prev) => ({ ...prev, rt: "all", q: undefined }),
      replace: true,
    });
  };
  const selectCarClass = (cls: CarClass) => {
    clearSearch();
    navigate({
      search: (prev) => ({ ...prev, cc: cls, q: undefined }),
      replace: true,
    });
  };
  const clearCarClass = () => {
    clearSearch();
    navigate({
      search: (prev) => ({ ...prev, cc: "all", q: undefined }),
      replace: true,
    });
  };
  const selectHunt = () => {
    const h = huntFilter()!;
    clearSearch();
    navigate({
      search: (prev) => ({
        ...prev,
        rt: h.raceType,
        cc: h.carClass,
        q: undefined,
      }),
      replace: true,
    });
  };

  const [isMobileOpen, setMobileOpen] = createSignal(false);

  return (
    <>
      <PageContainer>
        <div class="space-y-4">
          <div class="flex items-start justify-between gap-4">
            <PageHeader
              title="Rankings"
              description="Tier standings by race type and class"
            />
            <div class="shrink-0 pt-1 flex flex-col items-end gap-2">
              <PendingChangesButton />
              <Show when={config.data?.sheetsUrl}>
                {(url) => (
                  <a
                    href={url()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-1 text-label-xs text-text-faint hover:text-text-muted transition-colors"
                  >
                    <ExternalLink size={10} />
                    <Show when={activeRaceType()} fallback="View in Sheets">
                      {(rt) => <>{RACE_TYPE_LABELS[rt()]} rankings in Sheets</>}
                    </Show>
                  </a>
                )}
              </Show>
            </div>
          </div>

          <div class="flex justify-between">
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <div class="control-strip w-fit">
                  <For each={RACE_TYPES}>
                    {(type) => (
                      <ToggleChip
                        strip
                        active={activeRaceType() === type}
                        onClick={() => selectRaceType(type)}
                        class={cn(
                          activeSearch() && "opacity-40",
                          !activeSearch() &&
                            activeCarClass() !== undefined &&
                            !hasData(type, activeCarClass()!) &&
                            activeRaceType() !== type &&
                            "opacity-40",
                        )}
                      >
                        {RACE_TYPE_LABELS[type]}
                      </ToggleChip>
                    )}
                  </For>
                </div>
                <Show when={!activeSearch() && activeRaceType() !== undefined}>
                  <button
                    type="button"
                    onClick={clearRaceType}
                    class="pressable text-text-faint hover:text-text-base"
                    title="Show all race types"
                  >
                    <X size={14} />
                  </button>
                </Show>
              </div>

              <div class="flex items-center gap-2">
                <div class="control-strip w-fit">
                  <For each={CAR_CLASSES}>
                    {(cls) => (
                      <ToggleChip
                        strip
                        display
                        active={activeCarClass() === cls}
                        onClick={() => selectCarClass(cls)}
                        class={cn(
                          activeSearch() && "opacity-40",
                          !activeSearch() &&
                            activeRaceType() !== undefined &&
                            !hasData(activeRaceType()!, cls) &&
                            activeCarClass() !== cls &&
                            "opacity-40",
                        )}
                      >
                        {CAR_CLASS_LABELS[cls]}
                      </ToggleChip>
                    )}
                  </For>
                </div>
                <Show when={!activeSearch() && activeCarClass() !== undefined}>
                  <button
                    type="button"
                    onClick={clearCarClass}
                    class="pressable text-text-faint hover:text-text-base"
                    title="Show all car classes"
                  >
                    <X size={14} />
                  </button>
                </Show>
              </div>
            </div>
            <div class="hidden sm:flex flex-col items-end gap-2">
              <div class="relative w-52">
                <div class="relative">
                  <Show
                    when={activeSearch() && groups.isFetching}
                    fallback={
                      <Search
                        size={14}
                        class="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none"
                      />
                    }
                  >
                    <Loader2
                      size={14}
                      class="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand animate-spin pointer-events-none"
                    />
                  </Show>
                  <Input
                    type="text"
                    placeholder="Search member…"
                    value={searchInput()}
                    onInput={(e) => setSearchInput(e.currentTarget.value)}
                    class={cn(
                      "pl-8 pr-3 transition-colors",
                      activeSearch() ? "border-brand/50" : "border-border",
                    )}
                  />
                </div>
                <Show when={searchInput().trim().length > 0 && !activeSearch()}>
                  <p class="text-[10px] text-text-faint pl-1 leading-none absolute -top-4 m-0">Type at least 3 characters</p>
                </Show>
              </div>
              <Show when={huntFilter()}>
                <ToggleChip
                  active={isHuntActive()}
                  onClick={selectHunt}
                >
                  <Crosshair size={13} />
                  Closest Rivals
                </ToggleChip>
              </Show>
            </div>
          </div>
        </div>

        <Show
          when={!groups.isError}
          fallback={
            <div class="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-8 text-center space-y-3">
              <p class="text-sm text-red-400 font-semibold">Couldn't load rankings.</p>
              <p class="text-xs text-text-faint">{groups.error?.message ?? "The request failed. Check your connection and try again."}</p>
              <Button size="sm" variant="outline" onClick={() => groups.refetch()} disabled={groups.isFetching}>
                {groups.isFetching ? "Retrying…" : "Retry"}
              </Button>
            </div>
          }
        >
          <Show
            when={groups.data}
            fallback={
              <div class="rounded-xl border border-border overflow-hidden">
                <For each={Array.from({ length: 5 })}>
                  {() => (
                    <div class="flex gap-4 px-4 py-3 border-b border-border last:border-b-0 border-l-[3px] border-l-border">
                      <div class="w-30 shrink-0 space-y-1.5">
                        <div class="h-5 w-16 rounded bg-surface-3 animate-pulse" />
                        <div class="h-3 w-10 rounded bg-surface-3 animate-pulse" />
                      </div>
                      <div class="flex gap-1.5 flex-wrap flex-1">
                        <For each={Array.from({ length: 3 })}>
                          {() => (
                            <div class="h-8 w-32 rounded-lg bg-surface-3 animate-pulse" />
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            }
          >
            <TierLadder
              groups={filteredGroups()}
              userId={state.user?.id}
              activeRaceType={activeRaceType()}
              activeCarClass={activeCarClass()}
              profile={profile.data}
              searchTerm={activeSearch() || undefined}
            />
          </Show>
        </Show>
      </PageContainer>

      {/* ── Mobile: backdrop ────────────────────────────────────────────── */}
      <Show when={isMobileOpen()}>
        <div
          class="fixed inset-0 z-30 sm:hidden"
          onClick={() => setMobileOpen(false)}
        />
      </Show>

      {/* ── Mobile: slide-up panel ──────────────────────────────────────── */}
      <div
        class={cn(
          "fixed bottom-16 left-0 right-0 z-40 sm:hidden",
          "bg-surface-2 rounded-t-xl border-t border-border shadow-2xl p-4 space-y-3",
          "transition-transform duration-200",
          isMobileOpen() ? "translate-y-0" : "translate-y-full",
        )}
      >
        <div class="w-8 h-1 rounded-full bg-border mx-auto mb-2" />
        <div class="space-y-1">
          <div class="relative">
            <Show
              when={activeSearch() && groups.isFetching}
              fallback={
                <Search
                  size={14}
                  class="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none"
                />
              }
            >
              <Loader2
                size={14}
                class="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand animate-spin pointer-events-none"
              />
            </Show>
            <Input
              type="text"
              placeholder="Search member…"
              value={searchInput()}
              onInput={(e) => setSearchInput(e.currentTarget.value)}
              class={cn(
                "pl-8 pr-3 transition-colors",
                activeSearch() ? "border-brand/50" : "border-border",
              )}
            />
          </div>
          <Show when={searchInput().trim().length > 0 && !activeSearch()}>
            <p class="text-[10px] text-text-faint pl-1 leading-none">Type at least 3 characters</p>
          </Show>
        </div>
        <div class="flex gap-2">
          <Button
            ref={el => staggerButton = el}
            variant={isHuntActive() ? "brand-outline" : "secondary"}
            disabled={!huntFilter()}
            onClick={() => {
              selectHunt();
              setMobileOpen(false);
            }}
          >
            <Crosshair size={13} />
            Closest Rivals
          </Button>
        </div>
      </div>

      {/* ── Mobile: floating search trigger — hidden while panel is open ── */}
      <Button
        variant={activeSearch() || isHuntActive() ? "default" : "secondary"}
        onClick={() => setMobileOpen(v => !v)}
        class="fixed bottom-20 left-4 z-50 sm:hidden gap-2 shadow-lg transform translate-x-0 [will-change:transform]"
        style={{ ["--tw-translate-x" as string]: isMobileOpen() && staggerButton ? `${staggerButton.getBoundingClientRect().right - 8}px` : undefined }}
        aria-label="Search and filter"
      >
        <Dynamic component={isMobileOpen() ? X : Search} size={15} />
        <Show when={activeSearch()} fallback={<span>Search</span>}>
          <span class="max-w-20 truncate">{activeSearch()}</span>
        </Show>
      </Button>
    </>
  );
}
